import React from 'react';
import { t } from '@sm/intl';
import { createURL } from '@sm/utils';
import { LogoWithText, Treatment, UseExperimentResult } from '@sm/webassets';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';

import EndpageHeader from '~app/components/EndpageHeader';
import EndpageImage from '~app/components/EndpageImage';
import TextBanner from '~app/components/TextBanner';

import Pages, { Page } from './pages';

import useStyles from './useStyles';
import { STANDARD_ENDPAGE_DEFAULT_COPY } from './copy';

export type Props = {
  pageData?: Page;
  pageName: string;
  treatmentName?: string;
  treatments?: UseExperimentResult;
  utSource: string;
};

export default function StandardEndpage({
  pageData = Pages,
  pageName,
  treatmentName,
  treatments,
  utSource,
}: Props): React.ReactElement {
  const {
    imageSource,
    hasShadedBackground,
    logoRoute,
    pageCTA,
    pageCTARoute,
    pageHeading,
    pageSubheading,
    privacyNotice,
    textBannerBackgroundColor,
    textBannerCTA,
    textBannerCTARoute,
    textBannerHeading,
    textBannerSubheading,
    utSource2,
  } = pageData[pageName];

  const utSource3 = 'StandardEndpage';
  const { container, headingText, logo, subheadingText } = useStyles({ hasShadedBackground });

  return (
    <>
      <EndpageHeader
        copy={t(STANDARD_ENDPAGE_DEFAULT_COPY.ENDPAGE_HEADER)}
        hasLogo
        headerColor="sabaeus"
        headerType="thanks"
      />
      <Box className={container} display="flex" flexAlign="center" flexDirection="column" pt={6} px={5}>
        <Grid noBleeds noGutters>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
          <Grid.Item lg={6} md={10} xs={12}>
            <>
              <a
                aria-label={t(STANDARD_ENDPAGE_DEFAULT_COPY.LOGO_ARIA_LABEL)}
                data-testid="StandardEndpage__LogoWithText"
                href={createURL(logoRoute, {
                  ut_source: utSource,
                  ut_source2: utSource2,
                  ut_source3: utSource3,
                  ut_ctatext: 'LogoWithText',
                })}
              >
                <LogoWithText className={logo} />
              </a>
              <Box mt={7}>
                <Typography align="center" variant="sectionTitleSm">
                  <span className={headingText}>{t(pageHeading)}</span>
                </Typography>
              </Box>
              {pageSubheading && (
                <Box mt={2}>
                  <Typography align="center">
                    <span className={subheadingText}>{t(pageSubheading)}</span>
                  </Typography>
                </Box>
              )}
            </>
          </Grid.Item>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
        </Grid>
        <Box mt={5} mb={5}>
          {pageCTARoute && (
            <Button
              color="upgrade"
              href={createURL(pageCTARoute, {
                ut_source: utSource,
                ut_source2: utSource2,
                ut_source3: utSource3,
                ut_ctatext: t(pageCTA),
              })}
              size="lg"
            >
              {t(pageCTA)}
            </Button>
          )}
          {treatments && treatmentName && (
            <>
              <Treatment {...{ when: treatmentName, treatments: treatments.treatments }}>
                <Button
                  color="upgrade"
                  href={createURL(`/r/${treatmentName}`, {
                    ut_source: utSource,
                    ut_source2: utSource2,
                    ut_source3: utSource3,
                    ut_ctatext: t(pageCTA),
                  })}
                >
                  {t(pageCTA)}
                </Button>
              </Treatment>
            </>
          )}
        </Box>
        <Grid noBleeds noGutters>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
          <Grid.Item lg={6} md={10} xs={12}>
            <>
              {privacyNotice && (
                <Box mb={5}>
                  <Typography align="center">{t(...privacyNotice, { html: true })}</Typography>
                </Box>
              )}
            </>
          </Grid.Item>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
        </Grid>
      </Box>
      <Box mb={5}>
        <EndpageImage imageSource={imageSource} hasSplitBackgroundColor={hasShadedBackground} />
      </Box>
      {textBannerHeading ? (
        <TextBanner
          backgroundColor={textBannerBackgroundColor}
          ctaText={t(textBannerCTA)}
          heading={t(textBannerHeading)}
          route={textBannerCTARoute}
          subheading={t(textBannerSubheading)}
          utSource={utSource}
          utSource2={utSource2}
        />
      ) : (
        <TextBanner
          backgroundColor={textBannerBackgroundColor}
          ctaText={t(textBannerCTA)}
          route={textBannerCTARoute}
          subheading={t(textBannerSubheading)}
          utSource={utSource}
          utSource2={utSource2}
        />
      )}
    </>
  );
}
